import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../redux/store';
import { deleteDeviceType, fetchDeviceTypes } from '../../redux/DeviceTypes';
import { DeviceType, DeviceTypeListProps } from './types';
import Table from '../../commonComponents/Table';
import TablePagination from '../../commonComponents/Pagination';
import {
  NameLink,
  ActionButtons,
  ActionIconButton,
  StyledModal,
  ModalNote,
} from './styles';
import { LABELS, MESSAGES } from './constants';
import { ThemeProvider } from 'styled-components';
import { notification } from '../../commonComponents/Notification/Notification';

export const DeviceTypeList: React.FC<DeviceTypeListProps> = ({
  deviceTypes,
  onEdit,
  onAdd,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useSelector((state: RootState) => state.theme);
  const { loading } = useSelector((state: RootState) => state.deviceTypes);
  const themeMode = theme === 'dark' ? 'dark' : 'light';
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deviceTypeToDelete, setDeviceTypeToDelete] = useState<DeviceType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const paginatedDeviceTypes = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return deviceTypes.slice(startIndex, endIndex);
  }, [deviceTypes, currentPage, pageSize]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleDeleteClick = (deviceType: DeviceType) => {
    setDeviceTypeToDelete(deviceType);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deviceTypeToDelete) return;

    try {
      await dispatch(deleteDeviceType(deviceTypeToDelete.id)).unwrap();
      notification.success(MESSAGES.SUCCESS.DELETED);
      setDeleteModalVisible(false);
      setDeviceTypeToDelete(null);
      dispatch(fetchDeviceTypes());
    } catch (error: any) {
      notification.error(error || MESSAGES.ERROR.DELETE);
    }
  };

  const columns: ColumnsType<DeviceType> = [
    {
      title: LABELS.NAME,
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      sorter: (a, b) => {
        const aValue = a.name?.toLowerCase() || '';
        const bValue = b.name?.toLowerCase() || '';
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      },
      render: (text: string, record: DeviceType) => (
        <NameLink onClick={() => onEdit(record)}>{text}</NameLink>
      ),
    },
    {
      title: LABELS.ACTIONS,
      key: 'actions',
      align: 'left',
      render: (_: any, record: DeviceType) => (
        <ActionButtons>
          <ActionIconButton
            iconColor="#2D6CCA"
            onClick={() => onEdit(record)}
          >
            <FontAwesomeIcon icon={faPen} fontSize={14} color={themeMode === 'dark' ? '#fff' : '#222222'} />
          </ActionIconButton>
          <ActionIconButton
            iconColor="#f5222d"
            onClick={() => handleDeleteClick(record)}
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={themeMode === 'dark' ? '#fff' : '#222222'} />
          </ActionIconButton>
        </ActionButtons>
      ),
    },
  ];

  return (
    <ThemeProvider theme={{ mode: themeMode }}>
      <Table<DeviceType>
        columns={columns}
        dataSource={paginatedDeviceTypes}
        rowKey="id"
        loading={loading}
        pagination={false}
        emptyText={LABELS.NO_DEVICE_TYPES}
        emptyDescription={
          deviceTypes.length === 0
            ? LABELS.START_ADDING
            : LABELS.ADJUST_SEARCH
        }
        theme={themeMode}
        headerBackgroundColor={themeMode === 'dark' ? '#1a202c' : '#fafafa'}
        removeVerticalBorders={true}
        thPadding="15px 10px"
        tdPadding="10px 10px"
      />

      {deviceTypes.length > 0 && (
        <TablePagination
          totalCount={deviceTypes.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total device types"
          padding="16px 24px"
        />
      )}

      <StyledModal
        title={MESSAGES.CONFIRM.DELETE_TITLE}
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeviceTypeToDelete(null);
        }}
        okText={LABELS.DELETE}
        cancelText={LABELS.CANCEL}
        okButtonProps={{ danger: true }}
      >
        <div>
          <ModalNote>
            {MESSAGES.CONFIRM.DELETE_NOTE}
          </ModalNote>
          <p>
            {MESSAGES.CONFIRM.DELETE_MESSAGE} <strong>{deviceTypeToDelete?.name}</strong>?
          </p>
        </div>
      </StyledModal>
    </ThemeProvider>
  );
};
